import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CircleAlert } from 'lucide-react';
import IUCNLogo from 'src/assets/images/dashboard/iucn.png';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import DataTable from 'src/shared/components/data-table/DataTable';
import StatusChip from 'src/shared/components/status-chip/StatusChip';
import { Status } from 'src/shared/types';
import { PortfolioSpeciesSourceProvider } from 'src/app/portfolio/state/portfolioSpeciesSource';
import {
    DataTableColumns,
    DataTableState,
    SortingDirection,
    TableCellRendererParams,
    TableRowData,
    TableRowsSortingFunction,
} from 'src/shared/components/data-table/dataTableTypes';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import Alert from 'src/shared/components/ui/alert';
import Tabs from 'src/shared/components/Tabs/Tabs';
import IUCNStatusChip from 'src/shared/components/iucn-status-chip/IUCNStatusChip';
import { IUCNStatusCode } from 'src/shared/helpers/iucnStatusHelper';
import SpeciesImage from 'src/app/portfolio/components/species-image/SpeciesImage';
// The live page's own section: same wording, CMS explainer slugs and analytics
// event, so the two pages stay identical here while both exist.
import { useNextSpeciesSummaryQuery, useNextRedListSpeciesQuery, useNextInvasiveSpeciesQuery } from './state/api/nextSpeciesGraphSlice';
import { NextSpeciesListItem } from './state/api/nextSpeciesQuery';
import SpeciesInfoModal from '../species-risk-indicators/components/species-info-modal/SpeciesInfoModal';
import ReportingFrameworks from '../species-risk-indicators/components/reporting-frameworks/ReportingFrameworks';

const ROWS_PER_PAGE = 10;

const RED_LIST_TAB = 'iucn-red-list-species';
const INVASIVE_TAB = 'invasive-species';

const tabs = [
    {
        id: INVASIVE_TAB,
        label: (
            <div className='flex items-center gap-2'>
                <CircleAlert className='text-red-100' /> Invasive species
            </div>
        ),
    },
    {
        id: RED_LIST_TAB,
        label: (
            <div className='flex items-center gap-2'>
                <img src={IUCNLogo} alt='' className='w-[24px] h-[24x]' /> IUCN Red List species
            </div>
        ),
    },
];

/**
 * Column to sort field. Every value here is one Go accepts — it rejects anything
 * else rather than defaulting, so an unmapped column would raise an error instead
 * of quietly returning an arbitrary order.
 *
 * The old page maps its status column to `iucnStatus`, which Go does not accept,
 * and has an "Added" column sorting on `predictionCreatedAt`, which the grid has
 * no value for at all. Neither exists here.
 */
const columnIdToSortField: Record<string, string> = {
    speciesName: 'commonName',
    scientificName: 'scientificName',
    iucnStatus: 'iucnCategory',
    siteCoverage: 'siteCount',
};

/** Sorting happens in Go; keep row order so the server's page is what renders. */
const preserveServerSortedRows: TableRowsSortingFunction = rows => [...rows];

/**
 * Fills the empty-state slot rather than using the table's overlay, which can only
 * dim rows that are already there. A page or sort change clears `currentData`, so
 * the table has no rows and would otherwise say the portfolio has no species while
 * the next page is still in flight.
 */
const tableLoading = (
    <div className='flex justify-center py-10' data-testid='next-species-table-loading'>
        <Loader />
    </div>
);

/**
 * Unconditional, unlike the CMS-flag banner on the site characteristics page:
 * this page is internal by construction. It also says where the figures come
 * from, because they will not match the customer-facing page.
 */
const internalOnlyBanner = (
    <Alert severity='warning' data-testid='next-species-internal-only'>
        Internal only — not shown to customers. Species are read from the global prediction grid, not the portfolio pipeline behind the
        current page, so the figures will differ.
    </Alert>
);

type SpeciesRowMeta = {
    species: NextSpeciesListItem;
    percentCoverage: number;
};

/** What the species info modal needs from the row that opened it. */
type SelectedRowData = {
    commonName: string;
    scientificName: string;
    siteCoverageInfo: { percentCoverage: number; siteCount: number };
    status: string;
};

const getRowMeta = (rowData: TableRowData): SpeciesRowMeta | null => {
    const firstCell = rowData[0];
    if (!firstCell || typeof firstCell !== 'object' || !('data' in firstCell)) {
        return null;
    }
    const meta = (firstCell as { data?: SpeciesRowMeta }).data;
    return meta?.species ? meta : null;
};

const iucnStatusRenderer = (params: TableCellRendererParams) => {
    if (!getRowMeta(params.rowData)) {
        return null;
    }
    const status = params.cellData as string;
    return status ? <IUCNStatusChip status={status as IUCNStatusCode} /> : null;
};

const siteCoverageRenderer = (params: TableCellRendererParams) => {
    const meta = getRowMeta(params.rowData);
    if (!meta) {
        return null;
    }
    const { percentCoverage, species } = meta;
    return (
        <span className='flex items-cent gap-1'>
            <span className='font-bold'>{percentCoverage}%</span>({species.siteCount} site{species.siteCount === 1 ? '' : 's'})
        </span>
    );
};

/** No New/Updated badge: the grid has no per-species timestamps to derive one from. */
const speciesNameRenderer = (params: TableCellRendererParams) => {
    const meta = getRowMeta(params.rowData);
    if (!meta) {
        return null;
    }
    const commonName = params.cellData as string;
    return (
        <div className='flex items-center gap-2'>
            <SpeciesImage
                scientificName={meta.species.scientificName}
                alt={commonName}
                width='100px'
                height='56.25px'
                className='rounded-lg'
            />
            <span className='text-primary'>{commonName || meta.species.scientificName}</span>
        </div>
    );
};

const redListColumns: DataTableColumns = [
    { columnId: 'metadataRow' },
    {
        columnId: 'speciesName',
        title: 'Species name',
        width: '35%',
        renderer: speciesNameRenderer,
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
    {
        columnId: 'scientificName',
        title: 'Scientific name',
        width: '25%',
        bodyCellClassName: 'italic',
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
    {
        columnId: 'iucnStatus',
        title: 'IUCN Red List status',
        width: '20%',
        renderer: iucnStatusRenderer,
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
    {
        columnId: 'siteCoverage',
        title: 'Site coverage',
        width: '20%',
        renderer: siteCoverageRenderer,
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
];

/** Invasive species carry a category but the list is not about it, so it is omitted. */
const invasiveColumns: DataTableColumns = [
    { columnId: 'metadataRow' },
    { ...redListColumns[1], width: '45%' },
    { ...redListColumns[2], width: '30%' },
    { ...redListColumns[4], width: '25%' },
];

export const NextSpeciesRiskIndicators = () => {
    const { currentCustomerId } = useCustomer();
    // The summary's two species cards each explore into one of these tabs, so the tab
    // travels in the URL. Validated rather than trusted: an unknown value would select
    // no tab at all, leaving the page with an empty table and nothing highlighted.
    const [searchParams] = useSearchParams();
    const urlTab = searchParams.get('tab');
    const [selectedTab, setSelectedTab] = useState<string>(tabs.some(tab => tab.id === urlTab) ? (urlTab as string) : tabs[0].id);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('siteCount');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

    const isRedListTab = selectedTab === RED_LIST_TAB;

    const summaryQuery = useNextSpeciesSummaryQuery({ customerId: currentCustomerId }, { skip: !currentCustomerId });
    const listArgs = {
        customerId: currentCustomerId,
        pagination: { page: currentPage, pageSize: ROWS_PER_PAGE },
        sortBy: { field: sortField, order: sortOrder },
    };
    const redListQuery = useNextRedListSpeciesQuery(listArgs, { skip: !currentCustomerId || !isRedListTab });
    const invasiveQuery = useNextInvasiveSpeciesQuery(listArgs, { skip: !currentCustomerId || isRedListTab });

    const summary = summaryQuery.currentData;
    const activeList = isRedListTab ? redListQuery : invasiveQuery;
    const list = activeList.currentData;

    // Surfaced rather than softened: a failed grid read must not render as a
    // portfolio with no species.
    const error = summaryQuery.error || activeList.error;

    const totalItems = list?.pagination.total ?? 0;

    const tableData = useMemo(() => {
        if (!list) {
            return [];
        }
        const siteCount = summary?.siteCount ?? 0;
        return list.species.map(species => {
            const meta: SpeciesRowMeta = {
                species,
                percentCoverage: siteCount > 0 ? Math.floor((species.siteCount / siteCount) * 100) : 0,
            };
            if (isRedListTab) {
                return [{ data: meta }, species.commonName, species.scientificName, species.iucnRedListCategory, species.siteCount];
            }
            return [{ data: meta }, species.commonName, species.scientificName, species.siteCount];
        });
    }, [list, summary, isRedListTab]);

    const [selectedRowData, setSelectedRowData] = useState<SelectedRowData | null>(null);
    const [tableState, setTableState] = useState<Partial<DataTableState>>({
        fixedHeader: true,
        hiddenColumnIds: new Set(['metadataRow']),
        rowsPerPage: ROWS_PER_PAGE,
        sortedColumn: { columnId: 'siteCoverage', sortedDirection: SortingDirection.DESC },
    });

    useEffect(() => {
        setTableState(prev => ({
            ...prev,
            data: tableData,
            currentPageNumber: currentPage,
            totalPages: Math.ceil(totalItems / ROWS_PER_PAGE),
        }));
    }, [tableData, currentPage, totalItems]);

    const handleStateChange = (newState: Partial<DataTableState>) => {
        setTableState(newState);
        if (newState.currentPageNumber && newState.currentPageNumber !== currentPage) {
            setCurrentPage(newState.currentPageNumber);
        }
        if (newState.sortedColumn) {
            const field = columnIdToSortField[newState.sortedColumn.columnId] || 'siteCount';
            const order = newState.sortedColumn.sortedDirection === SortingDirection.ASC ? ('ASC' as const) : ('DESC' as const);
            if (field !== sortField || order !== sortOrder) {
                setSortField(field);
                setSortOrder(order);
                setCurrentPage(1);
            }
        }
    };

    if (summaryQuery.isFetching && !summary) {
        return <StandardAppLayout mainContent={<Loader />} title='Portfolio species risks' subTitle='' topBanner={internalOnlyBanner} />;
    }

    return (
        // The modal below is shared with the live page; the provider is what points its
        // species detail query at the grid rather than the Neo4j projection.
        <PortfolioSpeciesSourceProvider source='grid'>
            <StandardAppLayout
                title='Portfolio species risks'
                subTitle='Predicted species of interest across your portfolio'
                topBanner={internalOnlyBanner}
                mainContent={
                    error ? (
                        <Alert severity='error' data-testid='next-species-error'>
                            Species predictions are unavailable for this portfolio right now.
                        </Alert>
                    ) : (
                        <div className='flex flex-col gap-4'>
                            <div className='w-full border-b border-grey-20'>
                                <Tabs
                                    tabs={tabs}
                                    selectedTab={selectedTab}
                                    onTabChange={tab => {
                                        setSelectedTab(tab);
                                        setCurrentPage(1);
                                    }}
                                    classNames={{
                                        tabsContainer: 'flex items-center',
                                        default: 'px-3 py-4 text-[17px] text-black',
                                        selected: 'text-primary font-bold border-b-2 border-primary',
                                    }}
                                />
                            </div>

                            <div
                                className='flex items-center flex-wrap px-4 py-2 bg-grey-20 rounded-lg text-black text-sm'
                                data-testid='next-species-summary'
                            >
                                <span>
                                    {summary?.totalSpeciesCount ?? 0} predicted species across {summary?.siteCount ?? 0} sites
                                </span>
                                <span className='border-l border-grey-60 mx-5 w-[1px]'>&nbsp;</span>
                                {/* The current page's three figures: Red List count, Red List
                                sites, and the threatened subset. NT is on the Red List but
                                is not threatened, so both counts are shown. */}
                                <span data-testid='next-species-set-counts'>
                                    {isRedListTab
                                        ? `${summary?.redListSpeciesCount ?? 0} Red List species across ${
                                              summary?.redListSiteCount ?? 0
                                          } sites, of which ${summary?.threatenedSpeciesCount ?? 0} threatened`
                                        : `${summary?.invasiveSpeciesCount ?? 0} invasive species across ${
                                              summary?.invasiveSiteCount ?? 0
                                          } sites`}
                                </span>
                                <span className='border-l border-grey-60 mx-5 w-[1px]'>&nbsp;</span>
                                {/* The snapshot, not a run timestamp: it says which grid these figures came from. */}
                                <span data-testid='next-species-snapshot'>Grid: {summary?.snapshot || '—'}</span>
                                <span className='border-l border-grey-60 mx-5 w-[1px]'>&nbsp;</span>
                                <ReportingFrameworks />
                            </div>

                            {/* Reported, not hidden — otherwise the portfolio silently shrinks. */}
                            {!!summary?.sitesWithoutGeometry.length && (
                                <Alert severity='warning' data-testid='next-species-sites-without-geometry'>
                                    {summary.sitesWithoutGeometry.length} site
                                    {summary.sitesWithoutGeometry.length === 1 ? '' : 's'} have no boundary and are not included:{' '}
                                    {summary.sitesWithoutGeometry.map(site => site.siteName).join(', ')}
                                </Alert>
                            )}

                            <DataTable
                                key={selectedTab}
                                state={tableState}
                                columns={isRedListTab ? redListColumns : invasiveColumns}
                                onStateChange={handleStateChange}
                                onRowSelect={(rowData: TableRowData) => {
                                    const meta = getRowMeta(rowData);
                                    if (!meta) {
                                        return;
                                    }
                                    setSelectedRowData({
                                        commonName: rowData[1] as string,
                                        scientificName: rowData[2] as string,
                                        siteCoverageInfo: { percentCoverage: meta.percentCoverage, siteCount: meta.species.siteCount },
                                        status: isRedListTab ? meta.species.iucnRedListCategory : 'IN',
                                    });
                                }}
                                totalRowsForPagination={totalItems}
                                isServerSideSort
                                isLoadingOverlay={activeList.isFetching && !!list}
                                emptyStateMessage={
                                    activeList.isFetching
                                        ? tableLoading
                                        : `There are no ${
                                              isRedListTab ? 'IUCN Red List' : 'Invasive'
                                          } species predicted across this portfolio`
                                }
                                className='overflow-visible'
                                tableClassName='h-full'
                                allowSortClear={false}
                            />
                        </div>
                    )
                }
            />
            {selectedRowData && (
                <SpeciesInfoModal
                    commonName={selectedRowData.commonName}
                    scientificName={selectedRowData.scientificName}
                    siteCoverage={selectedRowData.siteCoverageInfo}
                    statusChip={
                        isRedListTab ? (
                            <IUCNStatusChip status={selectedRowData.status as IUCNStatusCode} className='text-xs' />
                        ) : (
                            <StatusChip status={Status.INVASIVE} label='Invasive' className='text-xs' />
                        )
                    }
                    onClose={() => setSelectedRowData(null)}
                />
            )}
        </PortfolioSpeciesSourceProvider>
    );
};

export default NextSpeciesRiskIndicators;
